'use client'

import { Cell, Spacer, Text } from '@vinted/web-ui'
import { useMemo, useRef } from 'react'
import { InView } from 'react-intersection-observer'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  getItemsByName,
  transformItemsBlockItemDtoToProductItem,
  ItemSearchParamsDto,
} from '@marketplace-web/item-page/item-page-data'
import { ProductItemModel } from '@marketplace-web/item-box/product-item-data'

import ItemViewItems from '../../components/ItemViewItems/ItemViewItems'
import { ItemPageSimilarItemsPluginModel } from './types'
import FeedSectionLayoutSkeleton from '../../layout/Feed/FeedSectionLayoutSkeleton'

type Props = {
  data?: ItemPageSimilarItemsPluginModel
}

type ItemsBlockItemModel = ProductItemModel & {
  searchTrackingParams?: ItemSearchParamsDto | null
}

const ItemPageSimilarItemsPlugin = ({ data }: Props) => {
  const { fetch: fetchSimilarItems, data: similarItemsData, isLoading } = useFetch(getItemsByName)
  const hasFetched = useRef(false)

  const translate = useTranslate('item')

  const items: Array<ItemsBlockItemModel> = useMemo(() => {
    return similarItemsData?.items.map(transformItemsBlockItemDtoToProductItem) || []
  }, [similarItemsData])

  if (!data) return null

  const handleInViewChange = (inView: boolean) => {
    if (!inView || hasFetched.current) return

    fetchSimilarItems({ itemId: data.itemId.toString(), name: 'similar_items' })
    hasFetched.current = true
  }

  const renderLoader = () => (!similarItemsData || isLoading) && <FeedSectionLayoutSkeleton />

  return (
    <InView as="section" data-testid="item-page-similar-items-plugin" onChange={handleInViewChange}>
      {renderLoader()}
      {items.length > 0 && (
        <>
          <Spacer size={Spacer.Size.XLarge} />
          <Cell
            title={
              <Text type={Text.Type.Title} as="h2">
                {translate('similar_items_title')}
              </Text>
            }
            styling={Cell.Styling.Tight}
            testId="item-page-similar_items_header-plugin"
          />
          <ItemViewItems items={items} contentSource="similar_items" itemTestId="similar-item" />
        </>
      )}
    </InView>
  )
}

export default ItemPageSimilarItemsPlugin
